/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useMemo } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericSearchbar, { ParsedFilter } from "@ui-components/Searchbar/GenericSearchbar";
import GenericButton from "@ui-components/Button/GenericButton";
import { ReactComponent as IconPlus } from "@shared/assets/Icons/IconPlusV3.svg";
import { clusterColumns, Cluster } from "./ClusterData";
import { getClusters } from "./request/request";
import ClusterModal from "./ClusterModal/ClusterModal";
import { useAuth0 } from "@auth0/auth0-react";
import EmptyItems from "@shared/components/EmptyItems";
import { filterClusters } from "@shared/components/utils/searchUtils";
import { useAppSelector } from "@shared/redux/hooks";

const suggestedKeys = [
  "Cluster ID",
  "Name",
  "Status",
  "Cloud",
  "Region",
  "Description",
  "Reflectors",
  "Last Sync",
];

const ClusterPage: React.FC = () => {
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isClusterModalOpen, setIsClusterModalOpen] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

	const { tenantId } = useAppSelector(state => state.selection);
  React.useEffect(() => {
    const fetchClusters = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        const res = await getClusters(token, tenantId);
        const mappedClusters = res.data.map((cluster: any) => ({
          ...cluster,
          reflectors: Array.isArray(cluster.instances)
            ? cluster.instances.map((inst: any) => inst.name)
            : [],
          status: cluster.status || "Unknown",
        }));
        setClusters(mappedClusters);
        
        // Set initial rows per page based on total data count
        if (mappedClusters.length > 5) {
          setRowsPerPage(10);
        } else {
          setRowsPerPage(5);
        }
      } catch (err) {
        setError("Failed to load clusters.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchClusters();
  }, [getAccessTokenSilently]);

  // Filtering logic using utility function
  const filteredData = useMemo(() => {
    return filterClusters(clusters, activeFilters);
  }, [activeFilters, clusters]);

  const handleCreateCluster = async (data: any) => {
    // TODO: Call createCluster API when available
    setIsClusterModalOpen(false);
    // TODO: Refresh cluster list after creation
  };

  if (isLoading) {
    return (
      <div
        className="fixed inset-0 flex flex-col items-center justify-center bg-black/50 z-[9999]"
      >
        <h2 className="text-xl font-semibold mb-4 text-white">
          Processing request...
        </h2>
        <div
          className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-500"
        />
      </div>
    );
  }
  if (error || clusters.length === 0) {
    return <EmptyItems />;
  }

  return (
    <div className="relative">
      <GenericCard isCollapsible={false}>
        {/* Searchbar and Create Cluster button (button below searchbar) */}
        <div className="mb-2 p-4">
          <div className="flex justify-end mb-4">
            <GenericButton
              label="Create Cluster"
              variant="brand"
              size="md"
              icon={<IconPlus className="w-5 h-5" />}
              iconPosition="left"
              onClick={() => setIsClusterModalOpen(true)}
            />
          </div>
          <GenericSearchbar
            placeholder="Find by attribute or value (case-sensitive)"
            suggestedKeys={suggestedKeys}
            onFiltersChange={setActiveFilters}
          />
        </div>
        {/* Table */}
        <GenericTable
          columns={clusterColumns}
          data={filteredData}
          initialRowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10]}
          width="100%"
          tableClassName="bg-semantic-bg-primary"
        />
      </GenericCard>
      <ClusterModal
        isOpen={isClusterModalOpen}
        onClose={() => setIsClusterModalOpen(false)}
        onSubmit={handleCreateCluster}
      />
    </div>
  );
};

export default ClusterPage;
